import * as Sentry from '@sentry/vue'

import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { IdsService } from '@/shared/service/utils.service'
import { create } from '@/shared/utils/zustandVue'

import {
  type Lang,
  makeTranslatableText,
} from '../../../../types/TranslatableText.type'
import { t } from '../../../i18n/i18n'
import { FestivalService } from '../../../shared/service/festival.service'
import { SnackbarService } from '../../../shared/snackbar/snackbar.service'
import { InternalType } from '../../../shared/types/files.type'
import { type AugmentedRequired, cloneDeep, fromEntries } from '../../../utils'
import { GeoJsonParser } from '../geojson.service'
import {
  type Coords,
  DEFAULT_MAP,
  type GeoJsonImportSummary,
  makeCoords,
  makePOI,
  makeZOI,
  type MapCopy,
  type MapEntityChoicesByType,
  type MapEntityType,
  type MapLimits,
  type MapObject,
  type MarkerIcon,
  type POI,
  type ZOI,
} from '../map.type'
import {
  estimateTileCount,
  getImageDimensions,
  type TileEstimationResult,
} from '../utils/tileEstimation'
import { MapService } from './../map.service'
import { useMapsStore } from './maps.store'

export type ForcedTileSaveMetadata = {
  estimation: TileEstimationResult
  dimensions: { width: number; height: number }
}

export enum MapModalStates {
  DELETE = 'DELETE',
  CHANGE = 'CHANGE',
  HIDE = 'HIDE',
}

export class MapState {
  zoiList: Record<string, ZOI> = {}

  map: MapObject | null = null
  mapCopy: MapCopy = {
    corners: [],
    imageOpacity: 100,
    mapOverlay: null,
    markerIcons: {},
    poiList: {},
    zoiList: {},
    bearing: 0,
    limits: null,
  }
  loading = false
  satelliteView = false

  //actions bar
  addingPoi = false
  addingZoi = false
  addingLayer = false
  rotatingMap = false
  editingLimits = false
  importGeoJson = false

  //markers
  markerIcons: Record<string, MarkerIcon> = {}
  editingMarkerId = ''
  pic = ''
  picCrop = ''

  //layer
  mapOverlay: string | null = null
  corners: Coords[] = []
  resetting = false
  showLayerModal: MapModalStates = MapModalStates.HIDE
  showLayerAlertModal = false
  warningModalType: 'save' | 'upload' = 'save'
  lastForcedSaveMetadata: ForcedTileSaveMetadata | null = null
  imageOpacity = 100

  //bearing
  bearing = 0

  //import
  showImportGeoJsonModal: MapModalStates = MapModalStates.HIDE
  importGeoJsonResult: GeoJsonImportSummary | null = null

  //limits
  setMapLimits = false
  limits: MapLimits | null = null

  preparedMap: MapObject | null = null

  mapFocus = ''

  newIcon = ''

  latLength = 0
  lngLength = 0

  //pois
  poiList: Record<string, POI> = {}

  //deleting modal
  type: 'ZOI' | 'POI' | 'MARKER' | 'LIMITS' | '' = ''
  deletingId = ''

  entityIdChoicesByType: MapEntityChoicesByType = {
    NONE: undefined,
    GUEST: undefined,
    SCENE: undefined,
  }
}

type MapStore = MapState & {
  setMapCopy: () => void
  resetMapModifications: () => void
  endReset: () => void
  changeMapView: () => void
  load: () => void
  unload: () => void
  setAddingPoi: () => void
  setAddingZoi: () => void
  setAddingLayer: () => void
  setRotatingMap: () => void
  setAddingNothing: () => void
  setBearing: (bearing: number) => void
  setShowLayerModal: () => void
  setShowChangeLayerModal: () => void
  setRemoveLayer: () => void
  opacityPlus: () => void
  opacityMinus: () => void
  setHideLayerModal: () => void
  setShowLayerAlertModal: (payload?: {
    type?: 'save' | 'upload'
    metadata?: ForcedTileSaveMetadata | null
  }) => void
  setHideLayerAlertModal: () => void
  addPoi: (payload: AugmentedRequired<Partial<POI>, 'coords'>) => void
  updatePoi: (payload: POI) => void
  setPoiCoords: (payload: { id: string; coords: Coords }) => void
  setPoiLat: (payload: { id: string; val: string }) => void
  setPoiLng: (payload: { id: string; val: string }) => void
  setPoiEntity: (payload: {
    poiId: string
    entityType: MapEntityType
    entityId: string
  }) => void
  setPoiLabel: (payload: { id: string; label: string; lang: Lang }) => void
  setPoiPic: (payload: {
    id: string
    value: { file: string; oid: string | null }
  }) => void
  setDeletePoi: (id: string) => void
  setDeleteMarker: (id: string) => void
  setEditingMarkerId: (id: string) => void
  setAddingMarker: () => void
  setPicCrop: (crop: string) => void
  addZoi: (payload: AugmentedRequired<Partial<ZOI>, 'zoneCorners'>) => void
  setDeleteZoi: (id: string) => void
  setDeleteLimits: () => void
  setZoiLabel: (payload: { id: string; label: string; lang: Lang }) => void
  setZoiEntity: (payload: {
    zoiId: string
    entityType: MapEntityType
    entityId: string
  }) => void
  setZoiColor: (payload: { id: string; color: string }) => void
  zoiEdited: (payload: {
    id: string
    latLngs: { lat: number; lng: number }[]
  }) => void
  setMapFocus: (id: string) => void
  cancelDeleting: () => void
  editLayer: (layer: {
    getCorners: () => { lat: number; lng: number }[] | null | undefined
  }) => void
  setCorners: (corners: Coords[]) => void
  resetCorners: () => void
  setMapOverlay: (mapOverlay: string) => void
  setEntityIdChoices: (choices: MapEntityChoicesByType) => void
  setImportGeoJson: (result: GeoJsonImportSummary | null) => void
  setShowImportGeoJsonModal: () => void
  setHideImportGeoJsonModal: () => void
  setEditingLimits: (value: boolean) => void
  setLimits: (limits: MapLimits | null) => void
  setMapLimitsFlag: (value: boolean) => void
  getMap: (payload: {
    id: string
    updateOnlyPreparedMap?: boolean
  }) => Promise<void>
  getMapEntityChoices: () => Promise<void>
  upsertMarker: (marker: MarkerIcon) => Promise<void>
  delete: () => Promise<void>
  executeSaveMap: () => Promise<void>
  save: () => Promise<void>
  forceSave: () => Promise<void>
  layerButtonPressed: () => void
  importGeoJsonFile: (payload: {
    file: Blob
    overwrite: boolean
  }) => Promise<void>
}

export const useMapStore = create<MapStore>((set, get) => ({
  ...new MapState(),

  setMapCopy: () => {
    const state = get()
    set({
      mapCopy: {
        poiList: cloneDeep(state.poiList),
        zoiList: cloneDeep(state.zoiList),
        markerIcons: cloneDeep(state.markerIcons),
        corners: cloneDeep(state.corners),
        mapOverlay: state.mapOverlay,
        imageOpacity: state.imageOpacity,
        bearing: state.bearing,
        limits: cloneDeep(state.limits),
      },
    })
  },

  resetMapModifications: () => {
    const state = get()
    set({
      resetting: true,
      poiList: cloneDeep(state.mapCopy.poiList),
      zoiList: cloneDeep(state.mapCopy.zoiList),
      markerIcons: cloneDeep(state.mapCopy.markerIcons),
      mapOverlay: state.mapCopy.mapOverlay,
      corners: cloneDeep(state.mapCopy.corners),
      imageOpacity: state.mapCopy.imageOpacity,
      bearing: state.mapCopy.bearing,
      limits: cloneDeep(state.mapCopy.limits),
      editingLimits: false,
    })
  },

  endReset: () => {
    set({ resetting: false })
  },

  changeMapView: () => {
    set({ satelliteView: !get().satelliteView })
  },

  load: () => {
    set({ loading: true })
  },

  unload: () => {
    set({ loading: false })
  },

  setAddingPoi: () => {
    set({
      addingPoi: !get().addingPoi,
      addingZoi: false,
      addingLayer: false,
      rotatingMap: false,
      editingLimits: false,
      showLayerModal: MapModalStates.HIDE,
    })
  },

  setAddingZoi: () => {
    set({
      addingPoi: false,
      addingZoi: !get().addingZoi,
      addingLayer: false,
      rotatingMap: false,
      editingLimits: false,
      showLayerModal: MapModalStates.HIDE,
    })
  },

  setAddingLayer: () => {
    set({
      addingPoi: false,
      addingZoi: false,
      addingLayer: !get().addingLayer,
      rotatingMap: false,
      editingLimits: false,
      showLayerModal: MapModalStates.HIDE,
      imageOpacity: 100,
    })
  },

  setRotatingMap: () => {
    set({
      addingPoi: false,
      addingZoi: false,
      addingLayer: false,
      rotatingMap: !get().rotatingMap,
      editingLimits: false,
      showLayerModal: MapModalStates.HIDE,
    })
  },

  setAddingNothing: () => {
    set({
      addingPoi: false,
      addingZoi: false,
      addingLayer: false,
      rotatingMap: false,
      editingLimits: false,
      showLayerModal: MapModalStates.HIDE,
    })
  },

  setBearing: (bearing) => {
    set({ bearing })
  },

  setShowLayerModal: () => {
    set({ showLayerModal: MapModalStates.DELETE })
  },

  setShowChangeLayerModal: () => {
    set({ showLayerModal: MapModalStates.CHANGE })
  },

  setRemoveLayer: () => {
    set({ corners: [], mapOverlay: null })
  },

  opacityPlus: () => {
    const opacity = get().imageOpacity
    if (opacity <= 80) {
      set({ imageOpacity: opacity + 20 })
    }
  },

  opacityMinus: () => {
    const opacity = get().imageOpacity
    if (opacity >= 40) {
      set({ imageOpacity: opacity - 20 })
    }
  },

  setHideLayerModal: () => {
    set({ showLayerModal: MapModalStates.HIDE })
  },

  setShowLayerAlertModal: (payload) => {
    set({
      showLayerAlertModal: true,
      warningModalType: payload?.type ?? 'save',
      lastForcedSaveMetadata: payload?.metadata ?? null,
    })
  },

  setHideLayerAlertModal: () => {
    set({ showLayerAlertModal: false, lastForcedSaveMetadata: null })
  },

  addPoi: (payload) => {
    const id = payload.oid ?? IdsService.generateId()
    const pois = cloneDeep(get().poiList)
    pois[id] = makePOI({
      ...payload,
      oid: id,
    })
    set({ poiList: { ...pois } })
  },

  updatePoi: (payload) => {
    const state = get()
    if (payload.oid in state.poiList) {
      const pois = { ...state.poiList }
      pois[payload.oid] = payload
      set({ poiList: pois })
    }
  },

  setPoiCoords: (payload) => {
    const state = get()
    if (payload.id in state.poiList) {
      const pois = cloneDeep(state.poiList)
      pois[payload.id].coords = payload.coords
      set({ poiList: pois })
    } else {
      const pois = { ...state.poiList }
      pois[payload.id] = makePOI({
        coords: payload.coords,
        labels: makeTranslatableText(payload.id),
      })
      set({ poiList: pois })
    }
  },

  setPoiLat: (payload) => {
    const tab = cloneDeep(get().poiList)
    tab[payload.id].coords.lat = Number(payload.val)
    set({ poiList: { ...tab } })
  },

  setPoiLng: (payload) => {
    const tab = cloneDeep(get().poiList)
    tab[payload.id].coords.lng = Number(payload.val)
    set({ poiList: { ...tab } })
  },

  setPoiEntity: (payload) => {
    const pois = cloneDeep(get().poiList)
    pois[payload.poiId].entityId = payload.entityId
    pois[payload.poiId].entityType = payload.entityType
    set({ poiList: pois })
  },

  setPoiLabel: (payload) => {
    const pois = cloneDeep(get().poiList)
    pois[payload.id].labels[payload.lang] = payload.label
    set({ poiList: pois })
  },

  setPoiPic: (payload) => {
    const tab = cloneDeep(get().poiList)
    tab[payload.id].pic = payload.value.file
    tab[payload.id].markerIconId = payload.value.oid
    set({ poiList: { ...tab } })
  },

  setDeletePoi: (id) => {
    set({ deletingId: id, type: 'POI' })
  },

  setDeleteMarker: (id) => {
    set({ deletingId: id, type: 'MARKER' })
  },

  setEditingMarkerId: (id) => {
    set({ editingMarkerId: id })
  },

  setAddingMarker: () => {
    set({ editingMarkerId: '' })
  },

  setPicCrop: (crop) => {
    set({ picCrop: crop })
  },

  addZoi: (payload) => {
    const id = payload.oid ?? IdsService.generateId()
    const zois = cloneDeep(get().zoiList)
    zois[id] = makeZOI({
      ...payload,
      oid: id,
    })
    set({ zoiList: { ...zois } })
  },

  setDeleteZoi: (id) => {
    set({ deletingId: id, type: 'ZOI' })
  },

  setDeleteLimits: () => {
    set({ deletingId: 'LIMITS', type: 'LIMITS' })
  },

  setZoiLabel: (payload) => {
    const zois = cloneDeep(get().zoiList)
    zois[payload.id].labels[payload.lang] = payload.label
    set({ zoiList: zois })
  },

  setZoiEntity: (payload) => {
    const zois = cloneDeep(get().zoiList)
    zois[payload.zoiId].entityId = payload.entityId
    zois[payload.zoiId].entityType = payload.entityType
    set({ zoiList: zois })
  },

  setZoiColor: (payload) => {
    const zois = cloneDeep(get().zoiList)
    zois[payload.id].color = payload.color
    set({ zoiList: { ...zois } })
  },

  zoiEdited: (payload) => {
    const zois = cloneDeep(get().zoiList)
    zois[payload.id].zoneCorners = payload.latLngs.map((latLng) =>
      makeCoords(latLng.lat, latLng.lng),
    )
    set({ zoiList: zois })
  },

  setMapFocus: (id) => {
    set({ mapFocus: id })
  },

  cancelDeleting: () => {
    set({ type: '', deletingId: '' })
  },

  editLayer: (layer) => {
    const corners = layer.getCorners()
    if (corners != null) {
      set({
        corners: cloneDeep(
          corners.map((corner: { lat: number; lng: number }) =>
            makeCoords(corner.lat, corner.lng),
          ),
        ),
      })
    }
  },

  setCorners: (corners) => {
    set({ corners: cloneDeep(corners) })
  },

  resetCorners: () => {
    set({ corners: [] })
  },

  setMapOverlay: (mapOverlay) => {
    set({ mapOverlay })
  },

  setEntityIdChoices: (choices) => {
    set({ entityIdChoicesByType: choices })
  },

  setImportGeoJson: (result) => {
    set({ importGeoJsonResult: result })
  },

  setShowImportGeoJsonModal: () => {
    set({ showImportGeoJsonModal: MapModalStates.CHANGE })
  },

  setHideImportGeoJsonModal: () => {
    set({ showImportGeoJsonModal: MapModalStates.HIDE })
  },

  setEditingLimits: (value) => {
    if (value) {
      set({
        editingLimits: value,
        addingPoi: false,
        addingZoi: false,
        addingLayer: false,
        rotatingMap: false,
      })
    } else {
      set({ editingLimits: value })
    }
  },

  setLimits: (limits) => {
    set({ limits })
  },

  setMapLimitsFlag: (value) => {
    const state = get()
    const { map } = state
    set({
      setMapLimits: value,
      map: map ? { ...map, setMapLimits: value } : map,
      editingLimits: value ? state.editingLimits : false,
    })
  },

  getMap: async ({ updateOnlyPreparedMap = false, id }) => {
    try {
      set({ loading: true })
      const [mapData, festival] = await Promise.all([
        MapService.getMapById(id),
        FestivalService.getFestival(),
      ])
      const markerIcons = festival.markerIcons.reduce<
        Record<string, MarkerIcon>
      >((acc, marker) => {
        if (marker.oid == null) return acc
        acc[marker.oid] = marker
        return acc
      }, {})

      if (!updateOnlyPreparedMap) {
        const poiList = mapData.poiList.reduce(
          (acc: Record<string, POI>, poi: POI) => {
            const isOrphan =
              poi.markerIconId != null &&
              !Object.prototype.hasOwnProperty.call(
                markerIcons,
                poi.markerIconId,
              )
            acc[poi.oid] = isOrphan
              ? {
                  ...poi,
                  pic: 'DEFAULT',
                  markerIconId: null,
                }
              : poi
            return acc
          },
          {},
        )
        const zoiList = mapData.zoiList.reduce(
          (acc: Record<string, ZOI>, zoi: ZOI) => {
            acc[zoi.oid] = zoi
            return acc
          },
          {},
        )
        set({
          preparedMap: mapData,
          markerIcons,
          map: mapData,
          poiList,
          zoiList,
          mapOverlay: mapData.mapOverlay ?? null,
          corners: mapData.corners,
          imageOpacity: mapData.imageOpacity,
          bearing: mapData.bearing,
          limits: mapData.limits ?? null,
          setMapLimits: mapData.setMapLimits,
          loading: false,
        })
      } else {
        set({ preparedMap: mapData, markerIcons, loading: false })
      }
    } catch (e) {
      set({ loading: false })
      ErrorService.handleError(e)
    }
  },

  getMapEntityChoices: async () => {
    try {
      const [sceneList, guestList] = await Promise.all([
        MapService.getSceneList(),
        MapService.getGuestList(),
      ])

      set({
        entityIdChoicesByType: {
          NONE: undefined,
          SCENE: sceneList,
          GUEST: guestList
            .filter((g): g is typeof g & { _id: string } => g._id != null)
            .map((g) => ({ _id: g._id, names: g.names })),
        },
      })
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  upsertMarker: async (marker) => {
    try {
      get().load()
      set({ editingMarkerId: marker.oid ?? '' })
      const image = await FilesService.uploadImage(
        FilesService.getImgFromCrop(get().picCrop, InternalType.MAP_MARKER),
      )
      if (marker.oid == null) return
      const state = get()
      set({
        markerIcons: {
          ...state.markerIcons,
          [marker.oid]: {
            ...marker,
            file: image.filename,
          },
        },
        poiList: fromEntries(
          Object.values(state.poiList).map((poi) => {
            if (poi.markerIconId === marker.oid) {
              return [
                poi.oid,
                {
                  ...poi,
                  pic: image.filename,
                },
              ] as const
            }
            return [poi.oid, poi] as const
          }),
        ),
        editingMarkerId: '',
      })
      await FestivalService.updateMarkerIcons(Object.values(get().markerIcons))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  delete: async () => {
    try {
      get().load()
      const state = get()
      const id = state.deletingId
      if (state.type === 'POI' && id in state.poiList) {
        const { [id]: _, ...pois } = state.poiList
        set({ poiList: pois })
      }
      if (state.type === 'ZOI' && id in state.zoiList) {
        const { [id]: _, ...zois } = state.zoiList
        set({ zoiList: zois })
      }
      if (state.type === 'LIMITS') {
        set({ limits: null })
      }
      if (state.type === 'MARKER') {
        const { [id]: _, ...icons } = state.markerIcons
        const poiList = fromEntries(
          Object.values(state.poiList).map((poi) => {
            if (poi.markerIconId === id) {
              return [
                poi.oid,
                {
                  ...poi,
                  pic: 'DEFAULT',
                  markerIconId: null,
                },
              ] as const
            }
            return [poi.oid, poi] as const
          }),
        )
        set({ markerIcons: icons, poiList })
        await FestivalService.updateMarkerIcons(
          Object.values(get().markerIcons),
        )
      }
      set({ deletingId: '' })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  executeSaveMap: async () => {
    get().load()
    const state = get()
    const preparedMap = computeGetPreparedMap(state)
    try {
      await MapService.saveMap(preparedMap)
      get().setMapCopy()
      SnackbarService.info(t('MAP.SNACKBAR_MSG'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      if (preparedMap._id != null) {
        await get().getMap({
          id: preparedMap._id,
          updateOnlyPreparedMap: true,
        })
      }
      const preparedMapAfter = get().preparedMap
      if (preparedMapAfter) {
        useMapsStore.getState().setEditing(preparedMapAfter)
      }
      get().unload()
    }
  },

  save: async () => {
    const state = get()
    if (state.mapOverlay != null && state.corners.length === 4) {
      try {
        const dimensions = await getImageDimensions(
          `/api/files/${state.mapOverlay}`,
        )
        const estimation = estimateTileCount(state.corners, dimensions)
        if (estimation.isExceedingThreshold) {
          get().setShowLayerAlertModal({
            type: 'save',
            metadata: { estimation, dimensions },
          })
          return
        }
      } catch (e) {
        console.warn('Failed to estimate tile count before saving:', e)
      }
    }

    await get().executeSaveMap()
  },

  forceSave: async () => {
    const state = get()
    const metadata = state.lastForcedSaveMetadata
    if (metadata) {
      console.warn(
        '[Map Tiling] Overlay save forced above estimated tile threshold:',
        {
          totalTiles: metadata.estimation.totalTiles,
          naturalMaxZoom: metadata.estimation.naturalMaxZoom,
          maxZoom: metadata.estimation.maxZoom,
          dimensions: metadata.dimensions,
          mapOverlay: state.mapOverlay,
          mapId: state.preparedMap?._id,
        },
      )
      Sentry.captureMessage(
        'Map overlay save forced above estimated tile threshold',
        {
          level: 'warning',
          extra: {
            totalTiles: metadata.estimation.totalTiles,
            naturalMaxZoom: metadata.estimation.naturalMaxZoom,
            maxZoom: metadata.estimation.maxZoom,
            imageWidth: metadata.dimensions.width,
            imageHeight: metadata.dimensions.height,
            mapOverlay: state.mapOverlay,
            mapId: state.preparedMap?._id,
          },
        },
      )
    }
    get().setHideLayerAlertModal()
    await get().executeSaveMap()
  },

  layerButtonPressed: () => {
    const state = get()
    if (!state.addingLayer && state.mapOverlay != null) {
      get().setShowChangeLayerModal()
    } else {
      get().setAddingLayer()
    }
  },

  importGeoJsonFile: async (payload) => {
    get().load()
    try {
      const geojson = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsText(payload.file)
        reader.onload = (): void => {
          resolve(typeof reader.result === 'string' ? reader.result : '')
        }
        reader.onerror = (error): void => {
          console.error(error)
          reject(
            error instanceof Error ? error : new Error('Error reading file'),
          )
        }
      })
      if (get().map?._id == null) throw new Error('Unknown map')
      const data = GeoJsonParser.parse(geojson)
      if (payload.overwrite) {
        set({ poiList: {}, zoiList: {} })
      }
      const markers = Object.values(get().markerIcons)
      data.pois.forEach((poi) => {
        get().addPoi(replaceMarkerImage(poi, markers))
      })
      data.zois.forEach((zoi) => {
        get().addZoi(zoi)
      })
      set({
        importGeoJsonResult: {
          pois: data.pois.length,
          zois: data.zois.length,
          errors: data.errors,
        },
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
}))

export function computeGetPreparedMap(state: MapState): MapObject {
  const preparedMap = state.preparedMap ?? {
    ...DEFAULT_MAP,
  }
  preparedMap.poiList = Object.values(state.poiList)
  preparedMap.zoiList = Object.values(state.zoiList)
  preparedMap.mapOverlay = state.mapOverlay
  preparedMap.corners = state.corners
  preparedMap.imageOpacity = state.imageOpacity
  preparedMap.bearing = state.bearing
  preparedMap.limits = state.limits

  // Config fields from maps store (includes unsaved config changes)
  const currentConfig = useMapsStore.getState().current
  if (currentConfig) {
    preparedMap.names = currentConfig.names
    preparedMap.hideLabels = currentConfig.hideLabels
    preparedMap.enableFilters = currentConfig.enableFilters
    preparedMap.setMapLimits = currentConfig.setMapLimits
    preparedMap.hideMapBackground = currentConfig.hideMapBackground
    preparedMap.hideGeolocalisation = currentConfig.hideGeolocalisation
    preparedMap.show = currentConfig.show
  }

  return preparedMap
}

// --- Helpers ---

function replaceMarkerImage(poi: POI, markers: MarkerIcon[]): POI {
  const marker = markers.find(
    (marker) =>
      marker.label.toLocaleLowerCase() === poi.pic.toLocaleLowerCase(),
  )
  if (marker) {
    poi.markerIconId = marker.oid
    poi.pic = marker.file
  } else {
    poi.markerIconId = null
    poi.pic = 'DEFAULT'
  }
  return poi
}
